import {
  isRecord,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireNumber,
  requireString,
  send,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

type Schemas = components['schemas']

export type InvitationRoleGrant = Omit<Schemas['InvitationGrantRequest'], 'grantType' | 'targetId' | 'entityId'> & {
  grantType: 'role'
  targetId: string
  entityId: string
}

export type InvitationGroupGrant = Omit<Schemas['InvitationGrantRequest'], 'grantType' | 'targetId' | 'entityId'> & {
  grantType: 'group'
  targetId: string
}

export type InvitationGrant = InvitationRoleGrant | InvitationGroupGrant

export type SendInvitationInput = Omit<Schemas['SendInvitationRequest'], 'grants'> & {
  email: string
  grants: InvitationGrant[]
}

export type SendInvitationResult =
  | (Schemas['SendInvitationResponse'] & { kind: 'invited'; invitationId: string })
  | (Schemas['SendInvitationDirectResponse'] & { kind: 'granted'; userId: string })

export type AcceptInvitationResponse = Schemas['AcceptInvitationResponse']

function decodeAcceptInvitation(raw: unknown): AcceptInvitationResponse {
  if (!isRecord(raw)) throw new Error('Malformed accept-invitation response')
  return {
    invitationId: requireString(raw.invitationId, 'invitationId'),
    zitadelInviteCode: nullableString(raw.zitadelInviteCode, 'zitadelInviteCode'),
  } satisfies AcceptInvitationResponse
}

export type SetPasswordSession = Schemas['SetPasswordResponse']

function decodeProbeInvitation(raw: unknown): boolean {
  if (!isRecord(raw)) throw new Error('Malformed probe-invitation response')
  const response = {
    setPassword: requireBoolean(raw.setPassword, 'setPassword'),
  } satisfies Schemas['ProbeInvitationResponse']
  return response.setPassword
}

function decodeSetPassword(raw: unknown): SetPasswordSession {
  if (!isRecord(raw)) throw new Error('Malformed set-password response')
  return {
    accessToken: requireString(raw.accessToken, 'accessToken'),
    expiresInSeconds: requireNumber(raw.expiresInSeconds, 'expiresInSeconds'),
  } satisfies SetPasswordSession
}

export type PendingAccessFailure = Schemas['PendingFailureResponse']

export type PendingAccessResult = Schemas['AcceptPendingInvitationsResponse']

function decodePendingAccessFailure(raw: unknown): PendingAccessFailure {
  if (!isRecord(raw)) throw new Error('Malformed pending-access failure')
  return {
    code: requireString(raw.code, 'code'),
    inviterName: requireString(raw.inviterName, 'inviterName'),
    inviterEmail: requireString(raw.inviterEmail, 'inviterEmail'),
  } satisfies PendingAccessFailure
}

function decodeAcceptPending(raw: unknown): PendingAccessResult {
  if (!isRecord(raw)) throw new Error('Malformed accept-pending response')
  if (!Array.isArray(raw.failures)) throw new Error('Malformed accept-pending failures')
  return {
    granted: requireBoolean(raw.granted, 'granted'),
    failures: raw.failures.map(decodePendingAccessFailure),
  } satisfies PendingAccessResult
}

export const invitationsApi = {
  acceptInvitation: (token: string) => request('/api/v1/invitations/accept', decodeAcceptInvitation, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token } satisfies Schemas['AcceptInvitationRequest']),
  }),
  acceptPendingInvitations: () =>
    request('/api/v1/invitations/accept-pending', decodeAcceptPending, { method: 'POST' }),
  probeInvitation: (token: string) => request('/api/v1/invitations/probe', decodeProbeInvitation, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token } satisfies Schemas['ProbeInvitationRequest']),
  }),
  setInvitationPassword: (token: string, password: string) => request('/api/v1/invitations/set-password', decodeSetPassword, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password } satisfies Schemas['SetPasswordRequest']),
  }),
  revokeInvitation: (entityId: string, invitationId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/invitations/${invitationId}`, { method: 'DELETE' }),
  resendInvitation: (entityId: string, invitationId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/invitations/${invitationId}/resend`, { method: 'POST' }),
  sendInvitation: async (input: SendInvitationInput): Promise<SendInvitationResult> => {
    const resp = await send('/api/v1/invitations', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    })
    const raw: unknown = await resp.json()
    if (!isRecord(raw)) throw new Error('Malformed send-invitation response')
    if (resp.status === 201) {
      const response = {
        invitationId: requireString(raw.invitationId, 'invitationId'),
      } satisfies Schemas['SendInvitationResponse']
      return { kind: 'invited', ...response } satisfies SendInvitationResult
    }
    const response = {
      userId: requireString(raw.userId, 'userId'),
    } satisfies Schemas['SendInvitationDirectResponse']
    return { kind: 'granted', ...response } satisfies SendInvitationResult
  },
}
